/**
 * Custom EventEmitter for browser environments
 * Provides event handling functionality for service classes
 */

export interface EventData<T = any> {
  data: T
  timestamp: Date
}

export type EventListener<T = any> = (event: EventData<T>) => void

export class EventEmitter {
  private listeners = new Map<string, Set<EventListener>>()

  /**
   * Add an event listener
   */
  on<T = any>(event: string, listener: EventListener<T>): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(listener)
  }

  /**
   * Remove an event listener
   */
  off<T = any>(event: string, listener: EventListener<T>): void {
    const eventListeners = this.listeners.get(event)
    if (eventListeners) {
      eventListeners.delete(listener)
      if (eventListeners.size === 0) {
        this.listeners.delete(event)
      }
    }
  }

  /**
   * Add a one-time event listener
   */
  once<T = any>(event: string, listener: EventListener<T>): void {
    const onceListener: EventListener<T> = (eventData) => {
      this.off(event, onceListener)
      listener(eventData)
    }
    this.on(event, onceListener)
  }

  /**
   * Emit an event to all listeners
   */
  protected emit<T = any>(event: string, data: T): void {
    const eventListeners = this.listeners.get(event)
    if (eventListeners) {
      const eventData: EventData<T> = {
        data,
        timestamp: new Date()
      }
      
      eventListeners.forEach(listener => {
        try {
          listener(eventData)
        } catch (error) {
          console.error(`Error in event listener for '${event}':`, error)
        }
      })
    }
  }

  /**
   * Remove all listeners for an event (or all events if no event specified)
   */
  removeAllListeners(event?: string): void {
    if (event) {
      this.listeners.delete(event)
    } else {
      this.listeners.clear()
    }
  }

  /**
   * Get the count of listeners for an event
   */
  listenerCount(event: string): number {
    const eventListeners = this.listeners.get(event)
    return eventListeners ? eventListeners.size : 0
  }

  /**
   * Get all event names that have listeners
   */
  eventNames(): string[] {
    return Array.from(this.listeners.keys())
  }

  /**
   * Clean up all listeners (useful for service destruction)
   */
  protected destroy(): void {
    this.removeAllListeners()
  }
}